'use client';

import { PropsWithChildren, useEffect } from 'react';
import dynamic from 'next/dynamic';
import { usePathname, useRouter } from 'next/navigation';
import { useIsRegistryRoute } from '@/hooks/useIsRegistryRoute';
import { useShell } from '@/providers';
import { Box, Stack } from '@/primitives';
import DesktopToolbar from './toolbar/DesktopToolbar';
import MobileToolbar from './toolbar/MobileToolbar';
import ShellSidebar from './ShellSidebar';
import ShellFooter from './ShellFooter';
import { MainContentErrorBoundary } from '@/components/ErrorBoundary/MainContentErrorBoundary';
import SplashScreen from '@/components/SplashScreen/SplashScreen';
import {
  FOOTER_HEIGHT,
  MOBILE_FOOTER_HEIGHT,
  NAV_HEIGHT,
  PAGE_PADDING,
  PAGE_PADDING_MOBILE,
  PAGE_PADDING_TOP,
} from '@/configs/layout';
import { PUBLIC_ROUTES } from '@/configs/routes';

// `GraphQLProvider` is dynamic-imported so the `@apollo/client` + entire
// `publicSearch/apolloClient.tsx` graph (~40 KB gzip) only ships in the
// chunks of routes that actually mount it. With `ssr: true` (default) the
// chunk is included in SSR + prefetched by `next/link`, so navigation to
// Apollo-needing routes is seamless in practice. The `loading` fallback
// covers the brief gap if a cold client-side nav has to fetch the chunk —
// without it, the toolbar's `useI4Links` query would render before Apollo
// context is ready and crash.
const GraphQLProvider = dynamic(() => import('@/providers/GraphQLProvider'), {
  loading: () => <SplashScreen />,
});

// Routes that do not query GraphQL — Apollo is skipped on these so the
// landing/legal pages don't load `@apollo/client` and the publicSearch
// graphql module graph. Everything else gets Apollo by default.
const APOLLO_FREE_PATHS = new Set([
  '/',
  // `/search` is the org-picker landing page. `isPublicSearch` is false here
  // (the toolbar never renders SocietySearchHeaderCenterDesktop), and
  // HeroSection's useGetSocietiesLazyQuery only fires when the user types —
  // Apollo is not needed for the initial paint. GraphQLProvider is instead
  // provided at the page level so the Shell renders without waiting for the
  // Apollo chunk.
  '/search',
  '/support',
  '/terms',
  '/privacy',
  '/logout',
  '/post-logout',
]);

function routeNeedsGraphQL(pathname: string): boolean {
  return !APOLLO_FREE_PATHS.has(pathname);
}

export function Shell({ children }: PropsWithChildren) {
  const pathname = usePathname();
  const router = useRouter();
  const isRegistryRoute = useIsRegistryRoute();
  const { isMobile } = useShell();
  const isFixedFooterPage =
    pathname === '/' ||
    pathname === '/registry' ||
    pathname === '/terms' ||
    pathname === '/privacy' ||
    pathname.endsWith('/account/api-documentation');

  useEffect(() => {
    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) router.refresh();
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, [router]);

  const needsGraphQL = routeNeedsGraphQL(pathname);
  const isHomeLanding = pathname === PUBLIC_ROUTES.HOME;
  // Home uses floating chrome over the hero — no reserved AppBar offset.
  const shellTopOffset = isHomeLanding ? 0 : NAV_HEIGHT;

  // Apollo has to wrap the toolbars too, not just the route content — the
  // toolbar's `Navigation` renders `SocietySearchHeaderCenterDesktop` on
  // public-search routes, which calls `useQuery` via `useI4Links`. Wrapping
  // only `children` left the toolbar without Apollo context and crashed.
  const shellInner = (
    <>
      {isMobile ? <MobileToolbar /> : <DesktopToolbar />}
      {isRegistryRoute ? (
        <Stack
          direction="row"
          sx={{
            height: `calc(100vh - ${NAV_HEIGHT}px - ${FOOTER_HEIGHT}px)`,
          }}
        >
          <ShellSidebar />
          <Box
            component="main"
            id="main"
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              pt: `${PAGE_PADDING_TOP}px`,
              px: {
                xs: `${PAGE_PADDING_MOBILE}px`,
                sm: `${PAGE_PADDING}px`,
              },
              pb: 0,
              height: `calc(100vh - ${NAV_HEIGHT}px - ${FOOTER_HEIGHT}px)`,
              overflowY: 'auto',
              overflowX: 'hidden',
            }}
          >
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
              }}
            >
              <MainContentErrorBoundary>{children}</MainContentErrorBoundary>
            </Box>
          </Box>
        </Stack>
      ) : (
        <Stack
          component="main"
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
          }}
        >
          <MainContentErrorBoundary>{children}</MainContentErrorBoundary>
        </Stack>
      )}
    </>
  );

  return (
    <Stack
      id="shell"
      sx={{
        flex: 1,
        mt: `${shellTopOffset}px`,
        ...(!isRegistryRoute && {
          // Ensure shell fills viewport on home; reserve space for fixed footer only there
          minHeight: `calc(100vh - ${shellTopOffset}px)`,
          height: {
            // prevent double scroll bar on android/ios devices
            md: `calc(100vh - ${shellTopOffset}px)`,
          },
          overflowY: 'auto',
          overflowX: 'hidden',
          ...(isFixedFooterPage && {
            pb: { xs: MOBILE_FOOTER_HEIGHT, md: FOOTER_HEIGHT },
          }),
        }),
      }}
    >
      {needsGraphQL ? (
        <GraphQLProvider>{shellInner}</GraphQLProvider>
      ) : (
        shellInner
      )}
      <ShellFooter fixed={isFixedFooterPage} />
    </Stack>
  );
}
